import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "../redux/store"; // Adjust the path as necessary
import { SERVER_URL } from "constants/production";

// NOTE:
// SERVER_URL is the single source of truth for the API base URL on the frontend.
// It is derived from REACT_APP_API_BASE_URL (and only falls back to dev/prod
// defaults when that env is missing). By using SERVER_URL here, all REST calls,
// Socket.IO connections, and fetch-based calls share the exact same origin.
const instance: AxiosInstance = axios.create({
  baseURL: SERVER_URL,
  timeout: 180 * 1000, // 180 seconds timeout (3 minutes) - increased for resume parsing
  headers: {
    // 'Connection': 'keep-alive', // Enable HTTP keep-alive
  },
});

// Setting the 'Accept-Encoding' header
// instance.defaults.headers.common["Accept-Encoding"] = "gzip, deflate, br";

// ============================================================================
// 🌐 NETWORK LOGGING - Commented out to reduce console noise
// Uncomment for debugging network issues
// ============================================================================
instance.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // const timestamp = new Date().toISOString();
    // console.log(`🚀 [NETWORK] REQUEST START: ${timestamp}`);
    // console.log(`🚀 [NETWORK] Method: ${config.method?.toUpperCase()}`);
    // console.log(`🚀 [NETWORK] URL: ${config.url}`);
    // console.log(`🚀 [NETWORK] Full URL: ${config.baseURL}${config.url}`);
    // console.log(`🚀 [NETWORK] Headers:`, config.headers);
    // console.log(`🚀 [NETWORK] Data Type:`, typeof config.data);

    // Handle FormData properly - don't try to JSON.stringify it
    if (config.data instanceof FormData) {
      // console.log(`🚀 [NETWORK] FormData detected`);
      // console.log(`🚀 [NETWORK] FormData keys:`, Array.from(config.data.keys()));
      // Don't set Content-Type for FormData - browser/axios will set it with boundary
      if (config.headers && config.headers['Content-Type']) {
        // Remove Content-Type if it's set to 'multipart/form-data' without boundary
        // Axios will add the boundary automatically
        delete config.headers['Content-Type'];
      }
    } else {
      // console.log(`🚀 [NETWORK] Data Size:`, config.data ? JSON.stringify(config.data).length : 0);
    }

    // Special logging for resume parsing requests - keep commented, uncomment when debugging
    // if (config.url?.includes('/cv/parser/parse/resume/')) {
    //   console.log(`🔥 [RESUME-PARSE] CRITICAL REQUEST DETECTED!`);
    //   console.log(`🔥 [RESUME-PARSE] URL: ${config.baseURL}${config.url}`);
    //   console.log(`🔥 [RESUME-PARSE] FormData keys:`, config.data instanceof FormData ? Array.from(config.data.keys()) : 'Not FormData');
    // }

    // Special logging for video uploads - keep commented, uncomment when debugging
    // if (config.url?.includes('/upload/interview-video')) {
    //   console.log(`🎥 [VIDEO-UPLOAD] Video upload request detected`);
    //   if (config.data instanceof FormData) {
    //     const entries = Array.from(config.data.entries());
    //     entries.forEach(([key, value]) => {
    //       if (value instanceof File) {
    //         console.log(`🎥 [VIDEO-UPLOAD] File:`, {
    //           key,
    //           name: value.name,
    //           type: value.type,
    //           size: value.size
    //         });
    //       }
    //     });
    //   }
    // }

    const token = store.getState()?.userReducer?.user?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const secureAdminToken = localStorage.getItem('keelzo_admin_secure_token');
    if (secureAdminToken) {
      config.headers['x-admin-secure-token'] = secureAdminToken;
    }

    config.metadata = { startTime: Date.now() };
    return config;
  },
  (error) => {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`❌ [NETWORK] REQUEST ERROR:`, error);
    }
    return Promise.reject(error);
  },
);

// Response interceptor for handling common errors
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    // const duration = response.config.metadata ? Date.now() - response.config.metadata.startTime : 0;
    // const timestamp = new Date().toISOString();
    // console.log(`✅ [NETWORK] RESPONSE SUCCESS: ${timestamp}`);
    // console.log(`✅ [NETWORK] Method: ${response.config.method?.toUpperCase()}`);
    // console.log(`✅ [NETWORK] URL: ${response.config.url}`);
    // console.log(`✅ [NETWORK] Status: ${response.status}`);
    // console.log(`✅ [NETWORK] Duration: ${duration}ms`);
    // console.log(`✅ [NETWORK] Response Size: ${JSON.stringify(response.data).length} bytes`);
    // console.log(`✅ [NETWORK] Response Data:`, response.data);

    // Special logging for resume parsing responses - keep commented, uncomment when debugging
    // if (response.config.url?.includes('/cv/parser/parse/resume/')) {
    //   console.log(`🔥 [RESUME-PARSE] CRITICAL RESPONSE RECEIVED!`);
    //   console.log(`🔥 [RESUME-PARSE] Status: ${response.status}`);
    //   console.log(`🔥 [RESUME-PARSE] Data Keys:`, Object.keys(response.data || {}));
    //   console.log(`🔥 [RESUME-PARSE] Full Response:`, response.data);
    // }

    return response;
  },
  (error) => {
    const isDev = process.env.NODE_ENV !== 'production';

    if (isDev) {
      const duration = error.config?.metadata ? Date.now() - error.config.metadata.startTime : 0;
      const timestamp = new Date().toISOString();
      console.log(`❌ [NETWORK] RESPONSE ERROR: ${timestamp}`);
      console.log(`❌ [NETWORK] Method: ${error.config?.method?.toUpperCase()}`);
      console.log(`❌ [NETWORK] URL: ${error.config?.url}`);
      console.log(`❌ [NETWORK] Full URL: ${error.config?.baseURL}${error.config?.url}`);
      console.log(`❌ [NETWORK] Status: ${error.response?.status}`);
      console.log(`❌ [NETWORK] Duration: ${duration}ms`);
      console.log(`❌ [NETWORK] Error Message: ${error.message}`);
      console.log(`❌ [NETWORK] Error Response:`, error.response?.data);
    }

    // Enhanced error message for connection refused
    if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error') || error.message.includes('ERR_CONNECTION_REFUSED')) {
      if (isDev) {
        console.error('⚠️ [NETWORK] Connection Refused - Backend server may not be running');
        console.error('   API Base URL:', error.config?.baseURL);
        console.error('   Please ensure:');
        console.error('   1. Backend server is running');
        console.error('   2. REACT_APP_API_BASE_URL is correctly set in .env');
        console.error('   3. Backend is accessible at the configured URL');
      }

      // Store error details for UI display
      error.isConnectionError = true;
      error.userFriendlyMessage = 'Unable to connect to server. Please ensure the backend server is running.';
    }

    // Enhanced error message for timeout
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      const timeoutDuration = error.config?.timeout || 180000;
      const timeoutSeconds = Math.round(timeoutDuration / 1000);
      if (isDev) {
        console.error(`⚠️ [NETWORK] Request Timeout - Request took longer than ${timeoutSeconds} seconds`);
        console.error('   URL:', error.config?.baseURL + error.config?.url);
        console.error('   This may indicate:');
        console.error('   1. Backend server is slow or unresponsive');
        console.error('   2. Network connectivity issues');
        console.error('   3. Backend endpoint is processing a long-running operation');
      }

      // Store error details for UI display
      error.isTimeoutError = true;
      error.userFriendlyMessage = `Request timed out after ${timeoutSeconds} seconds. The server may be slow or unresponsive.`;
    }

    if (isDev) {
      console.log(`❌ [NETWORK] Full Error:`, error);
    }

    // 403 entitlement handling: dispatch custom events for GlobalEntitlementListener
    if (error.response?.status === 403) {
      const data = error.response.data;
      if (data?.error === 'INSUFFICIENT_CREDITS') {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('keelzo:credits-exhausted', { detail: data }));
        }
      } else if (data?.error === 'FEATURE_NOT_AVAILABLE' || data?.error === 'LIMIT_EXCEEDED') {
        // Prevent global modal from popping up if the component has a specialized modal or inline UI
        const url = error.config?.url || '';
        const hasOwnModal = url.includes('/jobs/update') || url.includes('/jobs/publish') || url.includes('/scoring/candidate/score') || url.includes('/userInvitation') || url.includes('/candidate-assistant/chat');

        if (typeof window !== 'undefined' && !hasOwnModal) {
          // Resolve the correct requiredPlan from the capability key
          // so GlobalEntitlementListener shows "Upgrade to Growth" not "Upgrade to Pro"
          const CAPABILITY_TO_PLAN: Record<string, string> = {
            JD_AI_GENERATION: 'growth',
            PRACTICAL_ASSESSMENT: 'growth',
            FULL_AI_INSIGHTS: 'growth',
            CAREER_SITE_AI: 'growth',
            AI_KNOWLEDGE_BASE: 'growth',
            REDISCOVERY_DEEP: 'growth',
            CANDIDATE_DISCOVERY: 'pro',
            REDISCOVERY: 'pro',
            CANDIDATE_CHAT: 'pro',
            CANDIDATE_INVITE: 'pro',
            PRE_ASSESSMENT: 'pro',
            CANDIDATE_REPO_FULL: 'pro',
            AI_ASSISTANT_CONVERSATION: 'pro',
            AI_ASSISTANT_CONVERSATION_LIMIT: 'pro',
            INTERVIEW_SCHEDULING: 'pro',
          };
          const CAPABILITY_LABELS: Record<string, string> = {
            JD_AI_GENERATION: 'JD AI Generation',
            PRACTICAL_ASSESSMENT: 'Practical Assessment',
            FULL_AI_INSIGHTS: 'Full AI Insights',
            CAREER_SITE_AI: 'Career Site AI Chat',
            AI_KNOWLEDGE_BASE: 'AI Knowledge Base',
            REDISCOVERY_DEEP: 'Deep Rediscovery Insights',
            CANDIDATE_DISCOVERY: 'Candidate Discovery',
            REDISCOVERY: 'Rediscovery',
            CANDIDATE_CHAT: 'Candidate Chat',
            CANDIDATE_INVITE: 'Candidate Invite',
            PRE_ASSESSMENT: 'Pre-Assessment',
            CANDIDATE_REPO_FULL: 'Full Candidate Repo',
            AI_ASSISTANT_CONVERSATION: 'AI Assistant Conversations',
            AI_ASSISTANT_CONVERSATION_LIMIT: 'AI Assistant Conversations',
            INTERVIEW_SCHEDULING: 'Interview Scheduling',
          };
          const cap = data.capability || data.featureContext || '';
          const enrichedDetail = {
            ...data,
            requiredPlan: CAPABILITY_TO_PLAN[cap] || 'growth',
            capability: CAPABILITY_LABELS[cap] || cap || 'This feature',
          };
          window.dispatchEvent(new CustomEvent('keelzo:upgrade-required', { detail: enrichedDetail }));
        }
      }
    }

    // CREDIT SYSTEM: Handle insufficient credits globally
    const errorCode = error.response?.data?.error;
    if (errorCode === 'INSUFFICIENT_CREDITS' || errorCode === 'CREDIT_CHECK_FAILED') {
      const creditData = error.response?.data;
      // Attach credit-specific metadata for UI handling
      error.isCreditError = true;
      error.creditData = {
        required: creditData?.required,
        available: creditData?.available,
        action: creditData?.action,
        upgradeUrl: creditData?.upgradeUrl || '/dashboard/company/plans',
        boosterUrl: creditData?.boosterUrl || '/dashboard/credits/boosters',
      };
      error.userFriendlyMessage = creditData?.message || 'Insufficient AI credits for this action.';

      // Dispatch custom event for global credit alert component
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('credit:insufficient', {
          detail: error.creditData,
        }));
      }
    }

    // PRODUCTION-GRADE: Handle company deletion globally
    const isCompanyEndpoint = error.config?.url?.includes('/company/') ||
      error.config?.url?.includes('company/getcompany') ||
      error.config?.url?.includes('/userInvitation/list/');
    const status = error.response?.status;
    const message = error.response?.data?.message || error?.message || '';
    const isCompanyDeleted = status === 404 &&
      (message.toLowerCase().includes('not found') ||
        message.toLowerCase().includes('deleted')) &&
      isCompanyEndpoint;

    if (isCompanyDeleted) {
      if (isDev) console.warn("⚠️ [AXIOS] Company deleted detected - redirecting to login");
      // Only redirect if we're in the browser (not during SSR)
      if (typeof window !== 'undefined') {
        // Clear all storage
        localStorage.clear();
        sessionStorage.clear();
        // Redirect to login
        window.location.href = '/login';
      }
    }

    // Special logging for resume parsing errors - keep commented, uncomment when debugging
    // if (error.config?.url?.includes('/cv/parser/parse/resume/')) {
    //   console.log(`🔥 [RESUME-PARSE] CRITICAL ERROR!`);
    //   console.log(`🔥 [RESUME-PARSE] Status: ${error.response?.status}`);
    //   console.log(`🔥 [RESUME-PARSE] Error Data:`, error.response?.data);
    // }

    return Promise.reject(error);
  },
);

export default instance;
